import moment from "moment";
import _ from "i18n";

import { TTP_API_URL, TTP_BLOG_URL, S3_FOLDER_URL } from "../config";
import { ucFirst } from "./common";
import { prepareS3ResourceUrl } from "./event";
import { getGoPeopleUserNewsletterImage } from "../api/goPeople";

const NO_IMAGE = S3_FOLDER_URL + "/img/theme/no-theme.png";

// The API stores empty descriptions as the literal strings "undefined"/"null"
const isBlank = (value) =>
  typeof value !== "string" ||
  value.trim() === "" ||
  value.trim() === "undefined" ||
  value.trim() === "null";

const byLanguage = (channel, prefix, language) => {
  const suffixes = [ucFirst(language), "Fr", "En", "Nl"];
  for (const suffix of suffixes) {
    const value = channel[`${prefix}${suffix}`];
    if (!isBlank(value)) {
      return value;
    }
  }
  return "";
};

const CHANNEL_LOGO_HEIGHT = 180;
const EXPERT_PHOTO_HEIGHT = 600;
const MAX_EXPERTS = 4;
const MAX_REPLAY_FORMATIONS = 4;
const OFFFCOURSE_URL = "https://offfcourse.be";

const resizeLogo = (url, height = CHANNEL_LOGO_HEIGHT) => {
  if (!url) {
    return "";
  }
  const extension = url.match(/\.(png|jpe?g|webp|gif)$/i);
  const isMediaServer = /s3\.tamtam\.pro|amazonaws\.com/.test(url);

  if (!extension || !isMediaServer) {
    return url;
  }
  return `${url}/h${height}.${extension[1]}`;
};

const getMedia = (channel, type, language) => {
  const medias = channel.mediaChain || [];
  const ofType = medias.filter((media) => !media.type || media.type === type);
  const media = ofType.find((m) => m.language === language) || ofType[0];

  if (!media) {
    return "";
  }
  if (media.avatarUrl) {
    return media.avatarUrl;
  }
  return media.avatar ? `${TTP_API_URL}/${media.avatar}` : "";
};

const MAX_DESCRIPTION_LENGTH = 160;

const toExcerpt = (text = "", maxLength = MAX_DESCRIPTION_LENGTH) => {
  const firstParagraph =
    text
      .split(/\r\n|\r|\n/)
      .map((line) => line.trim())
      .find(Boolean) || "";

  if (firstParagraph.length <= maxLength) {
    return firstParagraph;
  }

  const cut = firstParagraph.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(" ");
  const truncated = lastSpace > 0 ? cut.slice(0, lastSpace) : cut;

  return `${truncated.replace(/[\s.,;:!?-]+$/, "")}…`;
};

const sanitizeForUrl = (name = "") =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const readHeadline = (raw) => {
  if (!raw) return "";
  if (typeof raw === "object") return raw.title || "";
  if (typeof raw !== "string") return "";

  const trimmed = raw.trim();
  if (!trimmed.startsWith("{")) return trimmed;

  try {
    return JSON.parse(trimmed).title || "";
  } catch (e) {
    return trimmed;
  }
};

const toExpert = (person, language) => {
  if (!person) return null;

  const firstName = person.firstName || "";
  const lastName = person.lastName || "";
  const name = `${firstName} ${lastName}`.trim();
  if (!name) return null;

  const headlineKey = `headline${ucFirst(language)}`;
  const headline = readHeadline(
    person.authorDetails?.[headlineKey] ?? person[headlineKey],
  );
  const url = person.blogUrlPath
    ? `${TTP_BLOG_URL}${language}${person.blogUrlPath}`
    : "";
console.log("===person", person);
  return {
    id: Number(person.id),
    userId: person.user?.id ?? person.userId ?? person.id,
    firstName,
    lastName,
    name,
    function: headline || person.signature?.head || "",
    photo: resizeLogo(
      person.avatarUrl || person.pictureUrl || "",
      EXPERT_PHOTO_HEIGHT,
    ),
    display: "block",
    url,
    urlLabel: url ? _("viewProfile", language) : "",
  };
};

const getEventSpeakers = (channel) => {
  const events = channel?.events || [];
  const now = moment();
  const sixMonthsFromNow = moment().add(6, "months");

  const activityCount = {};
  events.forEach((event) => {
    if (moment(event.startDateTime).isBetween(now, sixMonthsFromNow)) {
      (event["speakers-abstract"]?.speakers || []).forEach((speaker) => {
        if (speaker?.id) {
          activityCount[speaker.id] = (activityCount[speaker.id] || 0) + 1;
        }
      });
    }
  });

  const speakers = events.flatMap((event) =>
    (moment(event.startDateTime).isAfter(now) || event.isReplayable) &&
    Number(channel.organization) === Number(event.client)
      ? event["speakers-abstract"]?.speakers || []
      : [],
  );

  const unique = speakers.filter(
    (speaker, index, self) =>
      index === self.findIndex((s) => Number(s?.id) === Number(speaker?.id)),
  );

  return unique.sort(
    (a, b) => (activityCount[b?.id] || 0) - (activityCount[a?.id] || 0),
  );
};

const getArticleAuthors = (channel) => {
  const seen = new Set();

  return (channel?.articles || [])
    .flatMap((article) =>
      (article.author || []).map((author) => ({
        author,
        createdAt: article.createdAt,
      })),
    )
    .filter(
      (entry) =>
        entry.author?.enableAvatar &&
        !seen.has(entry.author.id) &&
        seen.add(entry.author.id),
    )
    .sort(
      (a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf(),
    )
    .map((entry) => entry.author);
};

export const getChannelExperts = (channel, language = "fr") => {
  const hiddenIds = (channel?.chainAuthors || [])
    .filter((link) => String(link.isHidden) === "1")
    .map((link) => Number(link.authorId));

  const candidates = [
    ...getEventSpeakers(channel).map((person) => ({
      person,
      isAuthor: false,
    })),
    ...getArticleAuthors(channel).map((person) => ({ person, isAuthor: true })),
  ];

  const seenIds = new Set();
  const seenNames = new Set();
  const experts = [];

  candidates.forEach(({ person, isAuthor }) => {
    if (experts.length >= MAX_EXPERTS) return;

    const expert = toExpert(person, language);
    if (!expert) return;

    if (isAuthor && hiddenIds.includes(expert.id)) return;

    const nameKey = expert.name.toLowerCase();

    if (seenIds.has(expert.id) || seenNames.has(nameKey)) return;

    seenIds.add(expert.id);
    seenNames.add(nameKey);
    experts.push(expert);
  });

  return experts;
};

export const enrichExpertsWithNewsletterImage = async (experts) => {
  if (!Array.isArray(experts) || experts.length === 0) return experts;

  const expertsByUserId = new Map();
  experts.forEach((expert) => {
    if (!expert?.userId) return;
    if (!expertsByUserId.has(expert.userId)) {
      expertsByUserId.set(expert.userId, []);
    }
    expertsByUserId.get(expert.userId).push(expert);
  });

  if (expertsByUserId.size === 0) return experts;

  await Promise.all(
    [...expertsByUserId.entries()].map(async ([userId, matching]) => {
      const newsletterImageUrl = await getGoPeopleUserNewsletterImage(userId);
      if (newsletterImageUrl) {
        matching.forEach((expert) => {
          expert.photo = newsletterImageUrl;
        });
      }
    }),
  );

  return experts;
};

const toReplayFormation = (event, language) => {
  if (!event) return null;

  const title = byLanguage(event, "name", language);
  if (!title) return null;

  const bannerPath = event.urlBannerFr || event.pictureUrlFr || "";
  const cover = prepareS3ResourceUrl(S3_FOLDER_URL, bannerPath) || NO_IMAGE;

  const kind = event.type === "formation" ? "event" : "cycle";
  const url = `${OFFFCOURSE_URL}/${language}/${kind}/${event.id}/reception`;

  const year = event.startDateTime
    ? moment(event.startDateTime).format("YYYY")
    : "";

  return {
    id: Number(event.id),
    title,
    cover,
    url,
    year,
    replayLabel: _("inReplay", language),
    urlLabel: _("replayFormation", language),
    display: "block",
  };
};

const getChannelReplayFormations = (channel, language) => {
  const events = channel?.events || [];

  return events
    .filter((event) => Number(event.isReplayable) === 1)
    .sort(
      (a, b) =>
        moment(b.startDateTime).valueOf() - moment(a.startDateTime).valueOf(),
    )
    .map((event) => toReplayFormation(event, language))
    .filter(Boolean)
    .slice(0, MAX_REPLAY_FORMATIONS);
};

export const formatChannel = (channel, language = "fr") => {
  if (!channel) {
    return null;
  }

  const name = byLanguage(channel, "name", language);
  const rawDescription = byLanguage(channel, "description", language);
  const logo = getMedia(channel, "AVATAR", language);

  return {
    id: channel.id,
    name,
    description: toExcerpt(rawDescription),
    logo: resizeLogo(logo) || NO_IMAGE,
    url: `${TTP_BLOG_URL}${language}/chain/${sanitizeForUrl(name)}/${channel.id}`,
    articlesCount: String(channel.articleCounter ?? 0),
    eventsCount: String(channel.eventCounter ?? 0),
    experts: getChannelExperts(channel, language),
    formations: getChannelReplayFormations(channel, language),
  };
};

export const getFirstArticleChannelId = (articles = []) => {
  for (const article of articles) {
    const channelId = article?.chains?.[0]?.id;
    if (channelId) {
      return channelId;
    }
  }
  return null;
};
